import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AuthContext from "../context/AuthContext";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const API_SENSORES = `${process.env.REACT_APP_BACKEND_URL || ""}/api/sensores`;

export default function CalibrationAdmin() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem("token");

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [loading, setLoading] = useState(false);
  const [calibrations, setCalibrations] = useState([]);
  const [msg, setMsg] = useState("");

  const [sensorsLoading, setSensorsLoading] = useState(false);
  const [availableSensors, setAvailableSensors] = useState([]);

  const [form, setForm] = useState({
    sensor_id: "",
    certificate_number: "",
    calibration_date: "",
    due_date: "",
    notes: "",
  });

  const [pdfFile, setPdfFile] = useState(null);

  const emptyPoint = {
  variable_name: "TEMP",
  test_value: "",
  item_reading: "",
  standard_reading: "",
  error_value: "",
  uncertainty: "",
  coverage_factor: "2",
  mpe: "",
  compliance: "Cumple",
  };

const calibrationVariableOptions = [
  { value: "TEMP", label: "Temperatura" },
  { value: "HUM", label: "Humedad" },
  { value: "PH", label: "pH" },
  { value: "TURBIDEZ", label: "Turbidez" },
  { value: "EC_K1", label: "Conductividad" },
  { value: "OTRO", label: "Otro" },
]
  const [pointsOpen, setPointsOpen] = useState(false);
  const [selectedCalibration, setSelectedCalibration] = useState(null);
  const [pointRows, setPointRows] = useState([{ ...emptyPoint }]);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsMsg, setPointsMsg] = useState("");

  const canManageCalibrations = Number(user?.id) === 1 && !!user?.permissions?.includes("manage_calibrations");

  const selectedSensorInfo = availableSensors.find(
  (s) => String(s.id) === String(form.sensor_id)
);

const loadAvailableSensors = async () => {
  setSensorsLoading(true);

  const types = [
    "HumedadTemperatura",
    "Temperatura",
    "CalidadAgua",
    "Nivel",
    "Flujo",
    "Ruido",
    "MapeoTermico",
    "GPS",
    "Medidores",
  ];

  try {
    const responses = await Promise.allSettled(
      types.map((type) =>
        axios.get(
          `${API_SENSORES}/devices?type=${encodeURIComponent(type)}`,
          authHeader
        )
      )
    );

    const allSensors = [];

    responses.forEach((result, index) => {
      if (result.status !== "fulfilled") return;

      const rows = Array.isArray(result.value.data)
        ? result.value.data
        : [];

      rows.forEach((s) => {
        allSensors.push({
          id: s.id,
          name: s.name || s.id,
          type: s.sensor_type || types[index],
          lat: s.lat,
          lng: s.lng,
        });
      });
    });

    const unique = new Map();

    allSensors.forEach((s) => {
      if (!s.id) return;
      unique.set(String(s.id), s);
    });

    setAvailableSensors(
      Array.from(unique.values()).sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      )
    );
  } catch (error) {
    setAvailableSensors([]);
  } finally {
    setSensorsLoading(false);
  }
};

  const loadCalibrations = async () => {
    setLoading(true);
    setMsg("");

    try {
      const { data } = await axios.get(`${API_ROOT}/calibrations`, authHeader);
      setCalibrations(Array.isArray(data) ? data : []);
    } catch (error) {
      setMsg(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "No se pudieron cargar las calibraciones."
      );
      setCalibrations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalibrations();
    loadAvailableSensors();  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setMsg("");

    if (!form.sensor_id.trim()) {
      setMsg("Debes ingresar el ID del sensor.");
      return;
    }

    if (!form.certificate_number.trim()) {
      setMsg("Debes ingresar el número de certificado.");
      return;
    }

    if (!form.calibration_date) {
      setMsg("Debes ingresar la fecha de calibración.");
      return;
    }

    if (!form.due_date) {
      setMsg("Debes ingresar la fecha de vencimiento.");
      return;
    }

    const fd = new FormData();
    fd.append("sensor_id", form.sensor_id.trim());
    fd.append("certificate_number", form.certificate_number.trim());
    fd.append("calibration_date", form.calibration_date);
    fd.append("due_date", form.due_date);
    fd.append("notes", form.notes || "");

    if (pdfFile) {
      fd.append("pdf", pdfFile);
    }

    setLoading(true);

    try {
      await axios.post(`${API_ROOT}/calibrations`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setMsg("✅ Certificado de calibración creado correctamente.");

      setForm({
        sensor_id: "",
        certificate_number: "",
        calibration_date: "",
        due_date: "",
        notes: "",
      });
      setPdfFile(null);

      await loadCalibrations();
    } catch (error) {
      setMsg(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "No se pudo crear la calibración."
      );
    } finally {
      setLoading(false);
    }
  };

   const normalizePointNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const openPointsModal = async (calibration) => {
  setSelectedCalibration(calibration);
  setPointsOpen(true);
  setPointsMsg("");
  setPointsLoading(true);

  try {
    const { data } = await axios.get(
      `${API_ROOT}/calibrations/${calibration.id}`,
      authHeader
    );

    const points =
      Array.isArray(data?.points)
        ? data.points
        : Array.isArray(data?.calibration_points)
        ? data.calibration_points
        : Array.isArray(data?.certificate?.points)
        ? data.certificate.points
        : [];

    if (points.length > 0) {
      setPointRows(
        points.map((p) => ({
          variable_name: p.variable_name || "TEMP",
          test_value: p.test_value ?? "",
          item_reading: p.item_reading ?? "",
          standard_reading: p.standard_reading ?? "",
          error_value: p.error_value ?? "",
          uncertainty: p.uncertainty ?? "",
          coverage_factor: p.coverage_factor ?? "2",
          mpe: p.mpe ?? "",
          compliance: p.compliance || "Cumple",
        }))
      );
    } else {
      setPointRows([{ ...emptyPoint }]);
    }
  } catch (error) {
    setPointRows([{ ...emptyPoint }]);
    setPointsMsg(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "No se pudieron cargar los puntos de calibración."
    );
  } finally {
    setPointsLoading(false);
  }
};

const closePointsModal = () => {
  if (pointsLoading) return;
  setPointsOpen(false);
  setSelectedCalibration(null);
  setPointRows([{ ...emptyPoint }]);
  setPointsMsg("");
};

const updatePointRow = (index, field, value) => {
  setPointRows((prev) =>
    prev.map((row, i) =>
      i === index
        ? {
            ...row,
            [field]: value,
          }
        : row
    )
  );
};

const addPointRow = () => {
  setPointRows((prev) => [...prev, { ...emptyPoint }]);
};

const removePointRow = (index) => {
  setPointRows((prev) => {
    const next = prev.filter((_, i) => i !== index);
    return next.length ? next : [{ ...emptyPoint }];
  });
};

const savePoints = async () => {
  if (!selectedCalibration?.id) return;

  setPointsMsg("");

  const cleanPoints = pointRows
    .map((p) => ({
      variable_name: String(p.variable_name || "").trim().toUpperCase(),
      test_value: normalizePointNumber(p.test_value),
      item_reading: normalizePointNumber(p.item_reading),
      standard_reading: normalizePointNumber(p.standard_reading),
      error_value: normalizePointNumber(p.error_value),
      uncertainty: normalizePointNumber(p.uncertainty),
      coverage_factor: normalizePointNumber(p.coverage_factor),
      mpe: normalizePointNumber(p.mpe),
      compliance: String(p.compliance || "").trim() || "Cumple",
    }))
    .filter((p) => p.variable_name && p.test_value !== null && p.error_value !== null);

  if (!cleanPoints.length) {
    setPointsMsg("Debes agregar al menos un punto con variable, punto de prueba y error.");
    return;
  }

  setPointsLoading(true);

  try {
    await axios.put(
      `${API_ROOT}/calibrations/${selectedCalibration.id}/points`,
      { points: cleanPoints },
      authHeader
    );

    setPointsMsg("✅ Puntos guardados correctamente.");
    await loadCalibrations();
  } catch (error) {
    setPointsMsg(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "No se pudieron guardar los puntos de calibración."
    );
  } finally {
    setPointsLoading(false);
  }
};  

   const handleDeactivate = async (id) => {
    if (!window.confirm("¿Seguro que deseas desactivar esta calibración?")) {
      return;
    }

    setLoading(true);
    setMsg("");

    try {
      await axios.delete(`${API_ROOT}/calibrations/${id}`, authHeader);
      setMsg("✅ Calibración desactivada correctamente.");
      await loadCalibrations();
    } catch (error) {
      setMsg(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "No se pudo desactivar la calibración."
      );
    } finally {
      setLoading(false);
    }
  };

  const styles = (
    <style>{`
      .cal-page{
        min-height:100vh;
        background:#eaf6ff;
        padding:28px;
        color:#0f172a;
        font-family:ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial;
      }

      .cal-top{
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:14px;
        flex-wrap:wrap;
        margin-bottom:18px;
      }

      .cal-title{
        margin:0;
        font-size:34px;
        font-weight:900;
        color:#0284c7;
      }

      .cal-subtitle{
        margin-top:4px;
        color:#475569;
        font-weight:700;
      }

      .cal-btn{
        border:none;
        border-radius:12px;
        padding:10px 14px;
        font-weight:900;
        cursor:pointer;
      }

      .cal-btn.primary{
        background:#2563eb;
        color:#fff;
      }

      .cal-btn.danger{
        background:#ef4444;
        color:#fff;
      }

      .cal-btn.ghost{
        background:#fff;
        color:#0f172a;
        border:1px solid #cbd5e1;
      }

      .cal-grid{
        display:grid;
        grid-template-columns:minmax(320px, 430px) 1fr;
        gap:18px;
        align-items:start;
      }

      .cal-card{
        background:#fff;
        border:1px solid #dbeafe;
        border-radius:18px;
        box-shadow:0 10px 26px rgba(2,6,23,.08);
        padding:18px;
      }

      .cal-card h2{
        margin:0 0 14px;
        font-size:20px;
        font-weight:900;
      }

      .cal-form{
        display:grid;
        gap:12px;
      }

      .cal-label{
        display:grid;
        gap:6px;
        font-size:13px;
        font-weight:900;
        color:#334155;
      }

      .cal-input,
      .cal-textarea{
        width:100%;
        box-sizing:border-box;
        border:1px solid #cbd5e1;
        border-radius:12px;
        padding:10px 12px;
        font-size:14px;
        font-weight:700;
        outline:none;
      }

      .cal-input:focus,
      .cal-textarea:focus{
        border-color:#60a5fa;
        box-shadow:0 0 0 3px rgba(59,130,246,.16);
      }

      .cal-textarea{
        min-height:82px;
        resize:vertical;
      }

      .cal-msg{
        margin:12px 0;
        font-weight:900;
        color:#0f172a;
      }

      .cal-table-wrap{
        overflow:auto;
      }

      .cal-table{
        width:100%;
        border-collapse:collapse;
        font-size:13px;
      }

      .cal-table th,
      .cal-table td{
        border-bottom:1px solid #e5e7eb;
        padding:10px;
        text-align:left;
        vertical-align:top;
      }

      .cal-table th{
        background:#f8fafc;
        font-weight:900;
        color:#334155;
      }

      .cal-badge{
        display:inline-flex;
        align-items:center;
        border-radius:999px;
        padding:4px 8px;
        font-size:12px;
        font-weight:900;
      }

      .cal-badge.active{
        background:#dcfce7;
        color:#166534;
      }

      .cal-badge.inactive{
        background:#fee2e2;
        color:#991b1b;
      }

   .cal-muted{
  color:#64748b;
  font-size:12px;
  font-weight:700;
}

.cal-sensor-preview{
  background:#f8fafc;
  border:1px solid #dbeafe;
  border-radius:14px;
  padding:10px 12px;
  color:#334155;
  font-size:13px;
  font-weight:700;
  display:grid;
  gap:4px;
}

.cal-actions{
  display:flex;
  gap:8px;
  flex-wrap:wrap;
}

.cal-btn.small{
  padding:7px 10px;
  border-radius:10px;
  font-size:12px;
}

.cal-modal-overlay{
  position:fixed;
  inset:0;
  background:rgba(2,6,23,.55);
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:3000;
  padding:18px;
}

.cal-modal{
  width:min(1100px, 96vw);
  max-height:90vh;
  overflow:auto;
  background:#fff;
  border-radius:18px;
  border:1px solid #dbeafe;
  box-shadow:0 24px 70px rgba(2,6,23,.24);
}

.cal-modal-head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px;
  padding:16px 18px;
  border-bottom:1px solid #e5e7eb;
  background:#f8fafc;
}

.cal-modal-title{
  font-size:20px;
  font-weight:900;
  margin:0;
}

.cal-modal-body{
  padding:18px;
}

.points-table{
  width:100%;
  border-collapse:collapse;
  font-size:12px;
}

.points-table th,
.points-table td{
  border-bottom:1px solid #e5e7eb;
  padding:8px;
  vertical-align:middle;
}

.points-table th{
  background:#f8fafc;
  text-align:left;
  font-weight:900;
  color:#334155;
}

.point-input{
  width:100%;
  min-width:90px;
  box-sizing:border-box;
  border:1px solid #cbd5e1;
  border-radius:10px;
  padding:8px 9px;
  font-weight:700;
  outline:none;
}

.point-input.var{
  min-width:95px;
}

.point-input.compliance{
  min-width:110px;
}

.points-footer{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
  margin-top:14px;
}

@media (max-width:900px){

        .cal-grid{
          grid-template-columns:1fr;
        }
      }
    `}</style>
  );

  if (!canManageCalibrations) {
    return (
      <div className="cal-page">
        {styles}
        <div className="cal-card">
          <h2>Acceso restringido</h2>
          <p>No tienes permiso para administrar calibraciones.</p>
          <button className="cal-btn ghost" onClick={() => navigate(-1)}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cal-page">
      {styles}

      <div className="cal-top">
        <div>
          <h1 className="cal-title">Calibraciones</h1>
          <div className="cal-subtitle">
            Administración de certificados, vencimientos y puntos de calibración.
          </div>
        </div>

        <button className="cal-btn ghost" onClick={() => navigate(-1)}>
          Volver
        </button>
      </div>

      {msg && <div className="cal-msg">{msg}</div>}

      <div className="cal-grid">
        <div className="cal-card">
          <h2>Nuevo certificado</h2>

          <form className="cal-form" onSubmit={handleCreate}>
          
         <label className="cal-label">
         Sensor calibrado
         <select
         className="cal-input"
         value={form.sensor_id}
         onChange={(e) => handleChange("sensor_id", e.target.value)}
         disabled={sensorsLoading}
         >
         <option value="">
         {sensorsLoading ? "Cargando sensores..." : "Selecciona un sensor"}
         </option>

         {availableSensors.map((sensor) => (
         <option key={sensor.id} value={sensor.id}>
         {sensor.name} — {sensor.id}
         </option>
         ))}
        </select>
        </label>

       {selectedSensorInfo && (
       <div className="cal-sensor-preview">
       <div>
       <strong>Sensor seleccionado:</strong> {selectedSensorInfo.name}
       </div>
       <div>
       <strong>DevEUI / ID:</strong> {selectedSensorInfo.id}
       </div>
       <div>
       <strong>Tipo:</strong> {selectedSensorInfo.type || "—"}
       </div>
       </div>
       )}

            <label className="cal-label">
              Número de certificado
              <input
                className="cal-input"
                value={form.certificate_number}
                onChange={(e) =>
                  handleChange("certificate_number", e.target.value)
                }
                placeholder="Ej: CGC-3790-125-25"
              />
            </label>

            <label className="cal-label">
              Fecha de calibración
              <input
                className="cal-input"
                type="date"
                value={form.calibration_date}
                onChange={(e) =>
                  handleChange("calibration_date", e.target.value)
                }
              />
            </label>

            <label className="cal-label">
              Fecha de vencimiento
              <input
                className="cal-input"
                type="date"
                value={form.due_date}
                onChange={(e) => handleChange("due_date", e.target.value)}
              />
            </label>

            <label className="cal-label">
              PDF del certificado
              <input
                className="cal-input"
                type="file"
                accept="application/pdf"
                onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
              />
            </label>

            <label className="cal-label">
              Notas
              <textarea
                className="cal-textarea"
                value={form.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder="Ej: Calibración TEMP/HUM del sensor AM102"
              />
            </label>

            <button
              className="cal-btn primary"
              type="submit"
              disabled={loading}
            >
              {loading ? "Guardando..." : "Guardar certificado"}
            </button>
          </form>
        </div>

        <div className="cal-card">
          <h2>Certificados registrados</h2>

          {loading && <div className="cal-muted">Cargando...</div>}

          <div className="cal-table-wrap">
            <table className="cal-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Sensor</th>
                  <th>Certificado</th>
                  <th>Calibración</th>
                  <th>Vence</th>
                  <th>Estado</th>
                  <th>PDF</th>
                  <th>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {calibrations.length === 0 && (
                  <tr>
                    <td colSpan="8" className="cal-muted">
                      No hay calibraciones registradas.
                    </td>
                  </tr>
                )}

                {calibrations.map((c) => (
                  <tr key={c.id}>
                    <td>{c.id}</td>
                    <td>{c.sensor_id}</td>
                    <td>
                      <strong>{c.certificate_number || "—"}</strong>
                      {c.notes && (
                        <div className="cal-muted">{c.notes}</div>
                      )}
                    </td>
                    <td>
                      {c.calibration_date
                        ? String(c.calibration_date).slice(0, 10)
                        : "—"}
                    </td>
                    <td>
                      {c.due_date ? String(c.due_date).slice(0, 10) : "—"}
                    </td>
                    <td>
                      <span
                        className={
                          c.active
                            ? "cal-badge active"
                            : "cal-badge inactive"
                        }
                      >
                        {c.active ? "Activa" : "Inactiva"}
                      </span>
                    </td>
                    <td>
                      {c.pdf_url ? (
                        <a href={c.pdf_url} target="_blank" rel="noreferrer">
                          Ver PDF
                        </a>
                      ) : c.original_filename ? (
                        c.original_filename
                      ) : (
                        "—"
                      )}
                    </td>
                    
                  <td>
                  <div className="cal-actions">
                  <button
                  className="cal-btn ghost small"
                  type="button"
                  onClick={() => openPointsModal(c)}
                  >
                  Puntos
                 </button>

                 {c.active ? (
                 <button
                 className="cal-btn danger small"
                 type="button"
                 onClick={() => handleDeactivate(c.id)}
                  >
                 Desactivar
                 </button>
                 ) : (
                 <span className="cal-muted">—</span>
                  )}
                 </div> 
                 </td>



                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12 }}>
            <button
              className="cal-btn ghost"
              type="button"
              onClick={loadCalibrations}
              disabled={loading}
            >
              Actualizar
            </button>
          </div>
        </div>
      </div>

     {pointsOpen && (
  <div className="cal-modal-overlay">
    <div className="cal-modal">
      <div className="cal-modal-head">
        <div>
          <h2 className="cal-modal-title">Puntos de calibración</h2>
          <div className="cal-muted">
            Certificado:{" "}
            <strong>
              {selectedCalibration?.certificate_number || selectedCalibration?.id}
            </strong>{" "}
            · Sensor: <strong>{selectedCalibration?.sensor_id || "—"}</strong>
          </div>
        </div>

        <button
          className="cal-btn ghost"
          type="button"
          onClick={closePointsModal}
          disabled={pointsLoading}
        >
          Cerrar
        </button>
      </div>

      <div className="cal-modal-body">
        {pointsMsg && <div className="cal-msg">{pointsMsg}</div>}

        <div className="cal-table-wrap">
          <table className="points-table">
            <thead>
           <tr>
          <th>Variable</th>
          <th>Punto calibrado</th>
          <th>Lectura sensor</th>
          <th>Lectura patrón</th>
          <th>Error</th>
          <th>Incertidumbre</th>
          <th>Acción</th>
          </tr>
            </thead>

            <tbody>
              {pointRows.map((p, index) => (
                <tr key={index}>
  
                 <td>
                 <select
                 className="point-input var"
                 value={p.variable_name}
                 onChange={(e) =>
                 updatePointRow(index, "variable_name", e.target.value)
                 }
                 >
                 {calibrationVariableOptions.map((opt) => (
                 <option key={opt.value} value={opt.value}>
                 {opt.label}
                 </option>
                  ))}
                 </select>
                 </td>

                  <td>
                    <input
                      className="point-input"
                      value={p.test_value}
                      onChange={(e) =>
                        updatePointRow(index, "test_value", e.target.value)
                      }
                      placeholder="Ej: 20"
                    />
                  </td>

                  <td>
                    <input
                      className="point-input"
                      value={p.item_reading}
                      onChange={(e) =>
                        updatePointRow(index, "item_reading", e.target.value)
                      }
                      placeholder="Ej: 20.2"
                    />
                  </td>

                  <td>
                    <input
                      className="point-input"
                      value={p.standard_reading}
                      onChange={(e) =>
                        updatePointRow(index, "standard_reading", e.target.value)
                      }
                      placeholder="Ej: 20.02"
                    />
                  </td>

                  <td>
                    <input
                      className="point-input"
                      value={p.error_value}
                      onChange={(e) =>
                        updatePointRow(index, "error_value", e.target.value)
                      }
                      placeholder="Ej: 0.18"
                    />
                  </td>

                  <td>
                    <input
                      className="point-input"
                      value={p.uncertainty}
                      onChange={(e) =>
                        updatePointRow(index, "uncertainty", e.target.value)
                      }
                      placeholder="Ej: 0.18"
                    />
                  </td>

                  <td>
                    <button
                      className="cal-btn danger small"
                      type="button"
                      onClick={() => removePointRow(index)}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="points-footer">
          <button
            className="cal-btn ghost"
            type="button"
            onClick={addPointRow}
            disabled={pointsLoading}
          >
            + Agregar punto
          </button>

          <button
            className="cal-btn primary"
            type="button"
            onClick={savePoints}
            disabled={pointsLoading}
          >
            {pointsLoading ? "Guardando..." : "Guardar puntos"}
          </button>
        </div>
      </div>
    </div>
  </div>
)}      

    </div>
  );
}
